// frontend/src/pages/portfolio/sections/FaqSection.tsx
import React from "react";
import { Helmet } from "react-helmet-async";
import { ChevronDown, HelpCircle } from "lucide-react";
import SectionEyebrow from "../components/SectionEyebrow";
import {
  cardHover,
  container,
  sectionHeading,
  sectionLede,
  sectionReveal,
} from "../components/styles";

const faqs: { q: string; a: string }[] = [
  {
    q: "Is Time Tracker really free?",
    a: "Yes. The full time-tracking platform — clock in/out, dashboards, calendar, screenshots, billing entry — is free with no credit card required. Only optional AI features are paid.",
  },
  {
    q: "What does the AI add-on cost?",
    a: "AI is pay-as-you-go. You only pay for the AI summary validations and session analyses your team actually uses. You can enable or disable AI per organization at any time.",
  },
  {
    q: "Do I have to install the desktop app?",
    a: "No. The desktop app gives the most accurate activity tracking via the OS, but admins can enable browser-based tracking instead, which works in modern Chromium browsers.",
  },
  {
    q: "How is my data stored and protected?",
    a: "Time data lives in a PostgreSQL database; screenshots are stored in AWS S3. All access is scoped by organization, role-based, and protected by JWT authentication. See our privacy policy for full details.",
  },
  {
    q: "Is there a team size limit?",
    a: "No imposed limit on the free platform. Add as many employees as your organization needs.",
  },
  {
    q: "Can I export my time data?",
    a: "Yes. Admins can export time entries, summaries, and reports from the dashboard for payroll or reporting workflows.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

const FaqSection: React.FC = () => {
  return (
    <section
      id="faq"
      className="border-t border-primary-200/30 bg-primary-50/30 py-16 sm:py-20"
      aria-labelledby="faq-heading"
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <div className={`${container} max-w-4xl`}>
        <div className={`max-w-2xl ${sectionReveal}`}>
          <SectionEyebrow icon={HelpCircle}>FAQ</SectionEyebrow>
          <h2 id="faq-heading" className={`mt-4 ${sectionHeading}`}>
            Common questions
          </h2>
          <p className={sectionLede}>
            Quick answers to what teams ask us most often.
          </p>
        </div>

        <ul className="mt-10 space-y-3">
          {faqs.map(({ q, a }) => (
            <li key={q}>
              <details
                className={`group rounded-2xl border border-gray-200 bg-white p-5 shadow-card ring-1 ring-primary-900/[0.04] sm:p-6 ${cardHover}`}
              >
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 font-display text-base font-semibold text-primary-950 sm:text-lg [&::-webkit-details-marker]:hidden">
                  <span>{q}</span>
                  <ChevronDown
                    className="mt-0.5 h-5 w-5 shrink-0 text-primary-600 transition-transform group-open:rotate-180"
                    aria-hidden
                  />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 sm:text-base">
                  {a}
                </p>
              </details>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default FaqSection;
